import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Injector,
  Output,
  ViewChild,
  afterNextRender,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { InfoBannerComponent, ModalComponent } from '@catch/general-ui';
import { TranslocoPipe } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { merge } from 'rxjs';
import { TFAService } from '../../../services/user/tfa.service';
import { ButtonComponent } from '../../button/button.component';
import { TfaInputComponent } from '../tfa-input/tfa-input.component';

@UntilDestroy()
@Component({
  selector: 'app-add-tfa-modal',
  templateUrl: './add-tfa-modal.component.html',
  styleUrls: ['./add-tfa-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    ModalComponent,
    InfoBannerComponent,
    TranslocoPipe,
    TfaInputComponent,
    ButtonComponent,
  ],
})
export class AddTfaModalComponent implements AfterViewInit {
  @ViewChild('addMethodModal') newMethodModal: ModalComponent;
  @ViewChild('tfaInput') tfaInput: TfaInputComponent;
  @Output() methodAdded: EventEmitter<void> = new EventEmitter<void>();

  public addMethodForm: FormGroup = new FormGroup({
    name: new FormControl('', [Validators.required]),
  });
  public addMethodFormSubmitAttempt: boolean = false;
  public tfaForm: FormGroup = new FormGroup({
    id: new FormControl('', [Validators.required]),
    tfaCode: new FormControl('', [Validators.required]),
  });
  public tfaFormSubmitAttempt: boolean = false;
  public tfaQRCodeImage: string;
  public tfaQRCode: string;
  public qrLoaded: boolean = false;
  public loadingQr: boolean = false;

  constructor(
    public tfaService: TFAService,
    public cdr: ChangeDetectorRef,
    private readonly injector: Injector,
  ) {}

  ngAfterViewInit() {
    merge(this.newMethodModal.onOpen, this.newMethodModal.onClose)
      .pipe(untilDestroyed(this))
      .subscribe(() => this.cdr.markForCheck());
  }

  onModalOpen(): void {
    this.reset();
    this.cdr.markForCheck();
  }

  onModalCloseEnd(): void {
    if (this.id.value) {
      this.tfaService.cancelAppCreation(this.id.value);
    }
    this.reset();
    this.cdr.markForCheck();
  }

  onContinue(): void {
    this.addMethodFormSubmitAttempt = true;
    if (!this.addMethodForm.valid) {
      this.cdr.markForCheck();
      return;
    }

    this.addMethodFormSubmitAttempt = false;
    this.loadingQr = true;
    this.tfaService.getAppCredential(this.name.value).subscribe({
      next: (result) => {
        this.tfaQRCode = result.code;
        this.tfaQRCodeImage = result['qr-code-image'];
        this.id.patchValue(result.id);
        this.qrLoaded = true;
        this.loadingQr = false;
        this.cdr.markForCheck();
        afterNextRender(() => this.tfaInput?.focus(), { injector: this.injector });
      },
      error: () => {
        this.loadingQr = false;
        this.cdr.markForCheck();
      },
    });
    this.cdr.markForCheck();
  }

  onTFASubmit(tfaValue: string): void {
    this.tfaCode.patchValue(tfaValue);
    this.tfaFormSubmitAttempt = true;
    if (this.tfaForm.valid) {
      this.tfaFormSubmitAttempt = false;
      this.tfaService
        .confirmAppCredential(this.id.value, this.tfaCode.value)
        .then(() => {
          this.tfaInput.validate(true);
          setTimeout(() => {
            this.id.patchValue('');
            this.newMethodModal.close();
            this.methodAdded.emit();
            this.cdr.markForCheck();
          }, 1000);
        })
        .catch(() => {
          this.tfaInput.validate(false);
        });
    }
    this.cdr.markForCheck();
  }

  private reset(): void {
    this.addMethodForm.reset();
    this.tfaForm.reset();
    this.qrLoaded = false;
    this.loadingQr = false;
    this.tfaQRCodeImage = undefined;
    this.tfaQRCode = undefined;
    this.addMethodFormSubmitAttempt = false;
    this.tfaFormSubmitAttempt = false;
    this.tfaInput?.reset();
  }

  get name() {
    return this.addMethodForm.get('name');
  }

  get tfaCode() {
    return this.tfaForm.get('tfaCode');
  }

  get id() {
    return this.tfaForm.get('id');
  }
}
